import React, { useRef, useState, useEffect, MouseEvent, ChangeEvent } from "react";
import "./BarcodeFormat.css";
import ImageTooltip from "../../../ImageTooltip/ImageTooltip";
import barcodeFormat, { FormatChildren } from "../../../../utils/barcodeFormat";
import { useSelector, useDispatch } from "react-redux";
import { animation, deepClone } from "../../../../utils/helpers";
import { updateGlobalSettings } from "../../../../store/features/globalSettingsSlice";
import { DivRef, DivArrRef } from "../../../../utils/typesAndMaps";
import { RootStore } from "../../../../store";

export default function BarcodeFormat() {
  const typesRef: DivRef = useRef(null);
  const childTypesRef: DivArrRef = useRef([]);
  const [isHideFormatList, setFormatListVisibility] = useState(false);
  const [bf, setBf] = useState(barcodeFormat);

  const globalSettings = useSelector((store: RootStore) => store.globalSettings);
  const dispatch = useDispatch();

  const globalSettingsRef = useRef(globalSettings);

  useEffect(()=>{
    globalSettingsRef.current = globalSettings;
  }, [globalSettings])

  useEffect(() => {
    let format: string[] = [];
    for (let i = 0; i < bf.length; i++) {
      if (bf[i].children) {
        for (let j = 0; j < bf[i].children!.length; j++) {
          if (bf[i].children![j].selected) {
            format.push(bf[i].children![j].value);
          }
        }
      } else {
        if (bf[i].selected) {
          if(bf[i].value instanceof Array) {
            format = format.concat(bf[i].value);
          } else {
            format.push(bf[i].value as string);
          }
        }
      }
    }
    const copySettings = deepClone(globalSettingsRef.current);
    copySettings.BarcodeFormatIds.value = format;
    dispatch(updateGlobalSettings({ settings: copySettings }));
  }, [bf, dispatch])

  useEffect(() => {
    const closeChildrenTypes = () => {
      for (let i = 0; i < childTypesRef.current!.length; i++) {
        childTypesRef.current![i].style.display = "none";
      }
    }
    window.addEventListener("click", closeChildrenTypes);
    return () => {
      window.removeEventListener("click", closeChildrenTypes);
    }
  }, [])

  const showChildrenFormat = (e: MouseEvent, element: HTMLDivElement) => {
    e.stopPropagation();
    for (let i = 0; i < childTypesRef.current!.length; i++) {
      if (childTypesRef.current![i] !== element) {
        childTypesRef.current![i].style.display = "none";
      }
    }
    animation(element, 3, true);
  }

  const changeBarcodeFormat = (e: ChangeEvent<HTMLInputElement>, isHasChildren: boolean, index: number) => {
    e.stopPropagation();
    bf[index].selected = !bf[index].selected
    if (isHasChildren) {
      bf[index].children?.forEach((item) => {
        item.selected = bf[index].selected;
      })
    }
    setBf([...bf]);
  }

  const changeChildrenFormat = (index: number, parentIndex: number) => {
    bf[parentIndex].children![index].selected = !bf[parentIndex].children![index].selected;
    let selectedCount = 0;
    const childrens = bf![parentIndex].children as FormatChildren;
    childrens.forEach((item) => { if (item.selected) selectedCount++; });
    const parentCheckbox = document.querySelectorAll(".parentCheckbox")[parentIndex] as HTMLInputElement; // todo
    if (selectedCount === childrens.length) {
      bf[parentIndex].selected = true;
      parentCheckbox.indeterminate = false;
    } else if (selectedCount === 0) {
      bf[parentIndex].selected = false;
      parentCheckbox.indeterminate = false;
    } else if (selectedCount > 0 && selectedCount < childrens.length) {
      parentCheckbox.indeterminate = true;
    }
    setBf([...bf]);
  }

  const renderBorcodeTypes = () => {
    return bf.map((item, index) => {
      return (
        <div key={item.id} className={`format dbr-${item.id}`} style={{ display: index > 3 && isHideFormatList ? "none" : "block" }}>
          <label htmlFor={item.id} onClick={(e) => { e.stopPropagation(); }}>
            <ImageTooltip title={`${item.id}.gif`}>
              <input type="checkbox" className="parentCheckbox" id={item.id} value={item.value} onChange={(e) => { changeBarcodeFormat(e, !!item.children, index) }} checked={item.selected} />
              {item.type}
            </ImageTooltip>
            {item.btnToggleId ? <button id={item.btnToggleId} className="dbr-btn-toggle" onClick={(e) => { showChildrenFormat(e, childTypesRef.current[item.index]) }}></button> : null}
          </label>
          {item.children ?
            <div className="dbr-barcode-children" ref={(node: HTMLDivElement) => { childTypesRef.current[item.index] = node }} style={{ display: "none" }} onClick={(e) => { e.stopPropagation(); }}>
              <strong>{item.type}</strong>
              {renderChildrenBorcodeTypes(item.children, index)}
            </div>
            :
            null
          }
        </div>
      )
    })
  }

  const renderChildrenBorcodeTypes = (barcodeFormat: FormatChildren, parentIndex: number) => {
    return barcodeFormat.map((item, index) => {
      return (
        <label key={item.id} style={{ display: "block" }} htmlFor={item.id}><input type="checkbox" value={item.value} id={item.id} className="ipt-barcodeFormat" checked={item.selected} onChange={() => { changeChildrenFormat(index, parentIndex) }} />{item.type}</label>
      )
    })
  }

  const hideTypes = () => {
    setFormatListVisibility(!isHideFormatList);
  }

  return (
    <div className="dbr-barcode-format">
      <div className="dbr-barcode-title">Select Barcode Type : </div>
      <div className="dbr-barcode-types" ref={typesRef}>
        {renderBorcodeTypes()}
        <div className="dbr-hide-types" onClick={hideTypes}>
          <span>{isHideFormatList ? "Show More Types" : "Hide Some Types"}</span>
          {
            isHideFormatList ?
              <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="3248" width="22" height="12"><path d="M116.736 282.624c8.192 0 16.384 3.072 22.528 9.216l372.736 372.736L884.736 291.84c6.144-6.144 13.824-9.216 22.528-9.216 8.192 0 16.384 3.072 22.528 9.216 12.288 12.288 12.288 32.256 0 44.544L534.528 732.16c-6.144 6.144-13.824 9.216-22.528 9.216-8.192 0-16.384-3.072-22.528-9.216L94.208 336.896c-12.288-12.288-12.288-32.256 0-44.544 6.144-6.144 14.336-9.728 22.528-9.728z" fill="#2C2C2C" p-id="3249"></path></svg>
              :
              <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2275" width="22" height="12"><path d="M534.6 403.5l294.2 294.2c12.5 12.5 32.8 12.5 45.3 0l0 0c12.5-12.5 12.5-32.8 0-45.3L557.3 335.6c-25-25-65.5-25-90.5 0L150 652.4c-12.5 12.5-12.5 32.8 0 45.3l0 0c12.5 12.5 32.8 12.5 45.3 0l294.1-294.2C501.9 391 522.1 391 534.6 403.5z" fill="#272636" p-id="2276"></path></svg>
          }
        </div>
      </div>
    </div>
  )
}
